import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {useHistory, useParams, withRouter} from 'react-router';
import {Segment} from 'semantic-ui-react';
import {useQuery} from '@apollo/client';

import {FilterQueryWrapper, FilterUrlParamsWrapper} from '@appComponents/HOCFiltersQueryWrapper';
import {getLink} from '@appComponents/Link';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {routes} from '@constants/routes';
import {convertToInt} from '@utils/helpers';
import {convertBooleanValueForFilters, getSearchFilters} from '@utils/filters';
import {clientBookingsForTable, clientBookingsDropdownOptions} from '@graphql/clients/query';
import {showConstraintsModal, skipSearch} from '@modules/client/utils/constraintsTables';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';
import {showErrorNoBookingsSelected} from '@modules/client/utils/bookingsBulkChange/errorModals';
import {
    updateClientBookingsState,
    updateDropdownValues,
    useClientBookingsRoutes,
    handleClientError,
} from '@modules/client/utils/clientBookingsHelper';
import {adjustHeight} from '@modules/client/utils/adjustHeightHelper';
import {useModalRouting} from '@utils/hooks/useModalRouting';

import ClientBookingsTable from '../components/ClientBookings/ClientBookingsTable';
import {ClientBookingsFiltersComponent} from '../components/ClientBookingsFilters';
import ClientBookingsButtons from '../components/ClientBookingsButtons';
import {exportExcel as clientBookingsExportExcel} from '../utils/export/clientBookingsExcel';

const CLIENT_BOOKINGS_INDEX = 'clients.bookings.index';
const DATA_ACTION = {
    BULK_CHANGE: 'bulkChange',
    BULK_CANCEL: 'bulkCancel',
};

const initialState = {
    bookingTypes: [],
    clientPackages: [],
    clientBookings: [],
    contentCategoriesLevel1: [],
    contentTiers: [],
    countries: [],
    deviceCategories: [],
    distributionTypes: [],
    eventTypes: [],
    invoiceStatuses: [],
    heightAdjusted: false,
    products: [],
    propertyLicences: [],
    propertyLicencesOriginal: [],
    sports: [],
    firstStageCategories: [],
    secondStageCategories: [],
    selectedElements: [],
    eventContentTypes: [],
    eventContentVariants: [],
};

export function ClientBookings(props) {
    const history = useHistory();
    const {id} = useParams();
    const [state, setState] = useState(initialState);
    const [bookingsInitialized, setBookingsInitialized] = useState(false);
    const defaultFilters = ClientBookings.defaultProps.filters;

    const {
        data: clientBookingsData,
        loading: clientBookingsLoading,
        error: clientBookingsError,
        refetch: refetchClientBookings,
    } = useQuery(clientBookingsForTable, {
        variables: {
            client: convertToInt(id),
            bookingTypes: props.filters.bookingTypes || defaultFilters.bookingTypes,
            clientPackages: props.filters.clientPackages || defaultFilters.clientPackages,
            isHq: convertBooleanValueForFilters(props.filters.isHq, null),
            isLl: convertBooleanValueForFilters(props.filters.isLl, null),
            contentTiers: props.filters.contentTiers || defaultFilters.contentTiers,
            countries: props.filters.countries || defaultFilters.countries,
            deviceCategories: props.filters.deviceCategories || defaultFilters.deviceCategories,
            distributionTypes: props.filters.distributionTypes || defaultFilters.distributionTypes,
            eventType: props.filters.eventType || defaultFilters.eventType,
            invoiceStatuses: props.filters.invoiceStatuses || defaultFilters.invoiceStatuses,
            products: props.filters.products || defaultFilters.products,
            propertyLicences: props.filters.propertyLicences || defaultFilters.propertyLicences,
            sports: props.filters.sports || defaultFilters.sports,
            firstStageCategories: props.filters.firstStageCategories || defaultFilters.firstStageCategories,
            secondStageCategories: props.filters.secondStageCategories || defaultFilters.secondStageCategories,
            startDate: props.filters.from ? `${props.filters.from}:00` : `${defaultFilters.from}:00`,
            endDate: props.filters.to ? `${props.filters.to}:00` : defaultFilters.to,
            tournamentCategories: props.filters.tournamentCategories || defaultFilters.tournamentCategories,
            tournaments: props.filters.tournaments || defaultFilters.tournaments,
            contentCategoryLevel1: props.filters.contentCategoriesLevel1 || defaultFilters.contentCategoriesLevel1,
            contentCategoryLevel2: props.filters.contentCategoriesLevel2 || defaultFilters.contentCategoriesLevel2,
            contentCategoryLevel3: props.filters.contentCategoriesLevel3 || defaultFilters.contentCategoriesLevel3,
            isCancelled: convertBooleanValueForFilters(props.filters.isCancelled, null),
            eventContentTypes: props.filters.eventContentTypes || defaultFilters.eventContentTypes,
            eventContentVariants: props.filters.eventContentVariants || defaultFilters.eventContentVariants,
        },
        skip: skipSearch(props),
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const {data: dropdownData,
        loading: dropdownLoading} = useQuery(clientBookingsDropdownOptions, {
        variables: {
            client: [convertToInt(id)],
        },
    });
    const loading = clientBookingsLoading || dropdownLoading;
    const cellRefs = useRef([]);

    const setCellRef = useCallback((el) => {
        if (null !== el && el.firstChild) {
            cellRefs.current.push(el.firstChild);
        }
    }, []);

    const onClickHandler = useCallback((e) => {
        const action = e.currentTarget.dataset.idkey;

        if (action === DATA_ACTION.BULK_CHANGE) {
            if (0 === state.selectedElements.length) {
                return showErrorNoBookingsSelected();
            }

            history.push(getLink('clients.bookings.bulkChange', {
                id: id,
                bookingsIds: state.selectedElements.join('+'),
            }));
        }

        if (action === DATA_ACTION.BULK_CANCEL) {
            if (0 === state.selectedElements.length) {
                return showErrorNoBookingsSelected(true);
            }

            history.push(getLink('clients.bookings.bulkCancel', {
                id: id,
                bookingsIds: state.selectedElements.join('+'),
            }));
        }
    }, [id, state.selectedElements]);

    useEffect(() => {
        setBookingsInitialized(false);

        if (skipSearch(props)) {
            showConstraintsModal(props.filters);
            setState(prev => ({...prev, clientBookings: initialState.clientBookings}));
            setBookingsInitialized(true);
        } else {
            refetchClientBookings();
        }
    }, [props.filters]);

    useEffect(() => {
        if (!clientBookingsLoading) {
            setState(prev => ({...prev, clientBookings: updateClientBookingsState(clientBookingsData)}));
            setBookingsInitialized(true);
        }
    }, [clientBookingsLoading]);

    const getSelectedElements = (selectedElements) => {
        setState({...state, selectedElements});
    };

    useModalRouting({
        routesMap: useClientBookingsRoutes(state.clientBookings, refetchClientBookings),
        shouldHandleModal: bookingsInitialized,
        indexUrl: getLink(CLIENT_BOOKINGS_INDEX, {id}),
    });

    useEffect(() => {
        if (!state.heightAdjusted) {
            const res = adjustHeight(cellRefs.current);

            if (res) {
                setState({...state, heightAdjusted: true});
            }
        }
    }, [state.heightAdjusted]);

    useEffect(() => {
        if(!dropdownLoading) {
            setState(prev => ({...prev, ...updateDropdownValues(dropdownData)}));
        }
    }, [dropdownLoading]);

    useEffect(() => {
        if (clientBookingsError) {
            handleClientError(clientBookingsError, 'bookings');
        }
    }, [clientBookingsError]);

    const clientId = convertToInt(id);
    const filtersProps = {
        additionalFilterId: id,
        data: {
            bookingTypes: state.bookingTypes,
            clientPackages: state.clientPackages,
            contentCategoriesLevel1: state.contentCategoriesLevel1,
            contentTiers: state.contentTiers,
            countries: state.countries,
            deviceCategories: state.deviceCategories,
            distributionTypes: state.distributionTypes,
            eventTypes: state.eventTypes,
            invoiceStatuses: state.invoiceStatuses,
            products: state.products,
            propertyLicences: state.propertyLicences,
            sports: state.sports,
            firstStageCategories: state.firstStageCategories,
            secondStageCategories: state.secondStageCategories,
            eventContentTypes: state.eventContentTypes,
            eventContentVariants: state.eventContentVariants,
        },
        loading: dropdownLoading,
        url: routes.clients.bookings.index.path,
    };

    return (
        <div>
            <HeaderRenderer
                buttons={ClientBookingsButtons}
                buttonsProps={{
                    onClickHandler: onClickHandler,
                    excelExport: clientBookingsExportExcel(state.clientBookings, clientId),
                    loading: loading,
                }}
                filters={ClientBookingsFiltersComponent}
                filtersProps={filtersProps}
            />
            <Segment basic className='--table'>
                <ClientBookingsTable
                    cellRefs={cellRefs.current}
                    clientBookings={state.clientBookings}
                    clientId={clientId}
                    loading={clientBookingsLoading}
                    refProperty={setCellRef}
                    getSelectedElements={getSelectedElements}
                    url={routes.clients.bookings.index.path}
                />
            </Segment>
        </div>
    );
}

ClientBookings.propTypes = {
    filters: PropTypes.shape({
        bookingTypes: PropTypes.arrayOf(PropTypes.number),
        clientPackages: PropTypes.arrayOf(PropTypes.number),
        isHq: PropTypes.number,
        isLl: PropTypes.number,
        contentCategories: PropTypes.arrayOf(PropTypes.number),
        contentTiers: PropTypes.arrayOf(PropTypes.number),
        countries: PropTypes.arrayOf(PropTypes.string),
        deviceCategories: PropTypes.arrayOf(PropTypes.number),
        distributionTypes: PropTypes.arrayOf(PropTypes.number),
        eventType: PropTypes.number,
        invoiceStatuses: PropTypes.arrayOf(PropTypes.number),
        products: PropTypes.arrayOf(PropTypes.number),
        propertyLicences: PropTypes.arrayOf(PropTypes.number),
        sports: PropTypes.arrayOf(PropTypes.number),
        firstStageCategories: PropTypes.arrayOf(PropTypes.number),
        secondStageCategories: PropTypes.arrayOf(PropTypes.number),
        tournamentCategories: PropTypes.arrayOf(PropTypes.number),
        tournaments: PropTypes.arrayOf(PropTypes.number),
        sport: PropTypes.arrayOf(PropTypes.number),
        eventContentTypes: PropTypes.arrayOf(PropTypes.number),
        eventContentVariants: PropTypes.arrayOf(PropTypes.number),
        contentCategoriesLevel1: PropTypes.arrayOf(PropTypes.number),
        contentCategoriesLevel2: PropTypes.arrayOf(PropTypes.number),
        contentCategoriesLevel3: PropTypes.arrayOf(PropTypes.number),
        from: PropTypes.string,
        to: PropTypes.string,
        isCancelled: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
    }),
};

ClientBookings.defaultProps = {
    filters: {
        bookingTypes: [],
        clientPackages: [],
        isHq: null,
        isLl: null,
        contentTiers: [],
        countries: [],
        deviceCategories: [],
        distributionTypes: [],
        eventType: null,
        invoiceStatuses: [],
        products: [],
        propertyLicences: [],
        sports: [],
        firstStageCategories: [],
        secondStageCategories: [],
        tournamentCategories: [],
        tournaments: [],
        eventContentTypes: [],
        eventContentVariants: [],
        contentCategoriesLevel1: [],
        contentCategoriesLevel2: [],
        contentCategoriesLevel3: [],
        from: getTodayDate(),
        to: getEndOfTodayDate(),
        isCancelled: 0,
    },
};

const ClientBookingsWrapped = FilterQueryWrapper(ClientBookings, {
    queryForRefresh: 'clientBookingsForTable',
    filterUrls: [CLIENT_BOOKINGS_INDEX],
});

const mapStateToProps = (state, ownProps) => ({
    filters: getSearchFilters(state, `ClientBookings${ownProps.match.params.id}`, ClientBookings.defaultProps.filters),
});

export default withRouter(connect(mapStateToProps)(
    FilterUrlParamsWrapper(ClientBookingsWrapped, ClientBookings.defaultProps.filters, CLIENT_BOOKINGS_INDEX)
));
